import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "./useAppContext";
import { DocumentMetadata, DocumentType } from "@/shared/types/documentTypes";
import { supabase } from "@/shared/lib/supabase";
import { getAccessToken } from "@/shared/types/auth";
import { getDocumentTypeLabel, normalizeDocumentType } from "@/shared/utils/documentTypeUtils";
import { logActivity } from "@/shared/utils/activityLogger";

const SETTLED_EXTRACTION_STATUSES = new Set(["completed", "failed", "error", "partial"]);
const DOCUMENTS_POLL_INTERVAL_MS = 5000;

interface Document {
  id: number;
  document_key: string;
  estate_id: number;
  uploaded_by_user_id: number;
  uploaded_by_name: string;
  document_type: string;
  file_name: string;
  display_name: string | null;
  uploaded_from_task_id: number | null;
  extraction_status: string;
  created_at: string;
  is_confirmation?: boolean;
  duplicate_of_name?: string | null;
  upload_session_id?: number | null;
  recipient_name?: string | null;
  folder_id?: number | null;
}

interface DocumentsResponse {
  documents: Document[];
  totalPages?: number;
  totalCount?: number;
}

interface UseDocumentsParams {
  estateId?: number;
}

// Map API Document to DocumentMetadata
function mapDocumentToMetadata(doc: Document): DocumentMetadata & {
  uploaded_by_user_id: number;
  uploaded_by_name: string;
  display_name: string | null;
} {
  return {
    id: doc.id,
    document_key: doc.document_key,
    user_email: "", // Not available in API response
    uploaded_by_email: "", // Not available in API response
    document_type: normalizeDocumentType(doc.document_type.toLowerCase()) as DocumentType,
    type_label: getDocumentTypeLabel(doc.document_type),
    file_name: doc.file_name,
    display_name: doc.display_name ?? null,
    file_size: 0, // Not available in API response
    created_at: doc.created_at,
    updated_at: doc.created_at, // Use created_at as fallback
    task_id: doc.uploaded_from_task_id || undefined,
    extraction_status: doc.extraction_status as DocumentMetadata["extraction_status"],
    uploaded_by_user_id: doc.uploaded_by_user_id,
    uploaded_by_name: doc.uploaded_by_name,
    is_confirmation: doc.is_confirmation,
    duplicate_of_name: doc.duplicate_of_name ?? null,
    upload_session_id: doc.upload_session_id ?? null,
    recipient_name: doc.recipient_name ?? null,
    folder_id: doc.folder_id ?? null,
  };
}

export function useDocuments(params?: UseDocumentsParams) {
  const { person } = useAppContext();
  const effectivePersonId = params?.estateId || person?.estate_id;

  return useQuery({
    queryKey: ["documents", effectivePersonId],
    queryFn: async (): Promise<{
      documents: (DocumentMetadata & {
        uploaded_by_user_id: number;
        uploaded_by_name: string;
        display_name: string | null;
      })[];
    }> => {
      if (!effectivePersonId) {
        throw new Error("Person ID is required to fetch documents");
      }

      const url = new URL(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${effectivePersonId}/list`,
      );

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication required to fetch documents");
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status} ${response.statusText}`);
      }

      const data: DocumentsResponse = await response.json();

      return {
        documents: data.documents.map(mapDocumentToMetadata),
      };
    },
    enabled: !!effectivePersonId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: (query) => {
      const documents = query.state.data?.documents;
      if (!documents?.length) return false;
      const isExtracting = documents.some(
        (doc) => !SETTLED_EXTRACTION_STATUSES.has(doc.extraction_status ?? ""),
      );
      return isExtracting ? DOCUMENTS_POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });
}

// Hook for getting document URL
export function useDocumentUrl() {
  const { authUser, person } = useAppContext();

  return useMutation({
    mutationFn: async (documentId: number): Promise<string> => {
      if (!authUser) {
        throw new Error("User must be authenticated");
      }

      if (!person?.estate_id) {
        throw new Error("Person ID is required");
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const url = new URL(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/documents/${person.estate_id}/document/${documentId}`,
      );

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get document URL");
      }

      const data = await response.json();
      return data.downloadUrl;
    },
  });
}

// Hook for deleting documents
export function useDocumentDelete() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: number) => {
      if (!person?.estate_id) {
        throw new Error("Person ID is required");
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/documents/${person.estate_id}/document/${documentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete document");
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch documents after successful deletion
      queryClient.invalidateQueries({
        queryKey: ["documents", person?.estate_id],
      });
      // Also invalidate StepWizard document queries
      queryClient.invalidateQueries({
        queryKey: ["estate_documents", person?.estate_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["estate_documents_wizard", person?.estate_id],
      });
    },
  });
}

// Hook for renaming documents
export function useDocumentRename() {
  const { person, user } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      newFileName,
    }: {
      documentId: number;
      newFileName: string;
    }) => {
      if (!person?.estate_id) {
        throw new Error("Person ID is required");
      }

      // Update display_name (user-facing name) instead of file_name (original filename)
      const { data, error } = await supabase
        .from("estate_documents")
        .update({ display_name: newFileName })
        .eq("id", documentId)
        .eq("estate_id", person.estate_id)
        .select()
        .single();

      if (error) {
        console.error("Error renaming document:", error);
        throw new Error(error.message || "Failed to rename document");
      }

      if (user?.user_id) {
        logActivity({
          estate_id: person.estate_id,
          action_type: "updated_document",
          user_id: user.user_id,
          entity_type: "document",
          entity_id: String(documentId),
          metadata: {
            display_name: newFileName,
            file_name: data.file_name,
            document_type: data.document_type,
          },
        }).catch(() => {});
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["documents", person?.estate_id],
      });
    },
  });
}
